<template>
  <div>
    <div class="hotel-selection">
      <form @submit.prevent="onCheckForm" class="hotel-selection__form">
        <div class="hotel-selection__main">
          <div class="hotel-selection__row hotel-selection__row--selects">
            <app-form-group label="Territory" required>
              <app-select
                @input="onTerritoryChanged"
                v-model="form.territory"
                :options="location_territories_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Territory"
                :error="$v.form.territory.$error"
              />
              <template #error>
                <div v-if="$v.form.territory.$dirty && !$v.form.territory.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Region" required>
              <app-select
                @input="onRegionChanged"
                v-model="form.region"
                :options="location_regions_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Region"
                :error="$v.form.region.$error"
                :readonly="!form.territory"
              />
              <template #error>
                <div v-if="$v.form.region.$dirty && !$v.form.region.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Area">
              <app-select
                v-model="form.area"
                :options="location_areas_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Area"
                :readonly="!form.region"
              />
            </app-form-group>
          </div>
          <div class="hotel-selection__row hotel-selection__row--dates">
            <app-form-group label="Check-in" class="form-group--no-indent" required>
              <app-date-picker
                @input="onSetCheckIn"
                v-model="form.check_in"
                placeholder="Check-in"
                :error="$v.form.check_in.$error"
              />
              <template #error>
                <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.required">Required field</div>
                <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.minValue">
                  Date cannot be less than the current date
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Check-out" class="form-group--no-indent" required>
              <app-date-picker
                v-model="form.check_out"
                placeholder="Check-out"
                :readonly="!form.check_in"
                :default-value="form.check_in"
                ref="check_out"
                :error="$v.form.check_out.$error"
              />
              <template #error>
                <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.required">Required field</div>
                <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.minValue">
                  Date cannot be less than the check-in date
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Nights" class="form-group--no-indent" disabled>
              <app-input :value="calcNumberOfNights" disabled />
            </app-form-group>
            <app-form-group label="Meal plan" class="form-group--no-indent" required>
              <app-select
                v-model="form.meal_plan"
                :reduce="item => item.code"
                :options="meal_plans_options"
                :clearable="false"
                placeholder="Meal plan"
                :error="$v.form.meal_plan.$error"
              />
              <template #error>
                <div v-if="$v.form.meal_plan.$dirty && !$v.form.meal_plan.required">Required field</div>
              </template>
            </app-form-group>
          </div>
        </div>
        <template v-if="accomodation_types.length">
          <app-room-icon-checkboxes @change="onTypeChanged" :list="accomodation_types" ref="checkboxes" />
        </template>
        <app-cells position="end" :indent="false">
          <app-button @click.native="onFormReset" theme="dark">Reset</app-button>
          <app-button type="submit" ref="submit">Search</app-button>
        </app-cells>
      </form>
      <app-cells><h2 class="title">Search result</h2></app-cells>
      <div v-if="search_result.length" class="hotel-selection__result">
        <div
          v-for="(hotel, index) in search_result"
          :key="`hotel_room_${index}`"
          class="hotel-selection__hotel"
        >
          <app-hotel-info-card :hotel="hotel" />
          <div v-for="(room, index) in hotel.rooms" :key="index" class="hotel-selection__room">
            <app-hotel-room
              :name="room.name"
              :room_type="room.room_type.name"
              :accomodation_type="room.accomodation_type"
              :price="room.price"
              :count="index + 1"
            />
          </div>
        </div>
      </div>
      <div v-else class="no-results">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No Results</div>
        <div class="no-results__text">There’s no available options. Set or change request and try again</div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getLocationTerritories,
  getLocationRegions,
  getLocationAreas,
  postHotelsSelection,
  getHotelsAccomodationTypes,
  getHotelsMealPlanTypes,
} from '@/http'
import { helpers, required } from 'vuelidate/lib/validators'
import AppRoomIconCheckboxes from '@/components/AppRoomIconCheckboxes.vue'
import AppHotelRoom from '@/components/AppHotelRoom.vue'
import AppHotelInfoCard from '@/components/AppHotelInfoCard.vue'
const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'HotelSelection',
  components: { AppHotelRoom, AppRoomIconCheckboxes, AppHotelInfoCard },
  data() {
    return {
      form: { check_in: null, check_out: null },
      location_territories_options: [],
      location_regions_options: [],
      location_areas_options: [],
      search_result: [],
      accomodation_types: [],
      meal_plans_options: [],
    }
  },
  validations: {
    form: {
      territory: { required },
      region: { required },
      meal_plan: { required },
      check_in: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      check_out: {
        required,
        minValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form.check_in).setHours(0, 0, 0, 0)) : true
        }
      },
    }
  },
  created() {
    getLocationTerritories().then(res => { this.location_territories_options = res.data })
    getHotelsMealPlanTypes().then(res => { this.meal_plans_options = res.data })
    getHotelsAccomodationTypes().then(res => { this.accomodation_types = res.data.map(item => ({ ...item, checked: false })) })
  },
  computed: {
    calcNumberOfNights() {
      if (this.form.check_in && this.form.check_out) {
        const date1 = new Date(this.form.check_in)
        const date2 = new Date(this.form.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    },
  },
  watch: {
    form: {
      handler() {
        if (this.search_result.length) this.search_result = []
      },
      deep: true
    }
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postHotelsSelection(this.form)
        .finally(() => { this.$refs.submit.preload = false })
        .then(res => {
          this.search_result = res.data
        })
    },
    onTypeChanged(checked) {
      this.form.accomodation_types = checked.map(item => item.code)
    },
    onSetCheckIn() {
      this.form.check_out = null
      this.$refs.check_out.$children[0].focus()
    },
    onFormReset() {
      this.form = { check_in: null, check_out: null }
      this.$v.form.$reset()
      this.$refs.checkboxes.resetSearchResult()
    },
    onTerritoryChanged() {
      this.form = { ...this.form, region: null, area: null }
      getLocationRegions({ territory: this.form.territory })
        .then(res => { this.location_regions_options = res.data })
    },
    onRegionChanged() {
      this.form = { ...this.form, area: null }
      getLocationAreas({ region: this.form.region })
        .then(res => { this.location_areas_options = res.data })
    },
  }
}
</script>
